/* eslint react/prop-types: 0 */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import Icon from '@material-ui/core/Icon';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Properties, { ROW_TYPES } from '../properties/Properties';
import { PageLink } from '../links/Links';
import { isBlank } from './properties/Funcs';
import { runTimeProperty } from './properties/RunTimeProperty';
import { runIntervalProperty } from './properties/RunIntervalProperty';
import usePanelDataService from '../../services/PanelDataService';
import { useTest } from '../filters';
import { DEFAULT_INVALID_VALUE } from '../../utilities/FormattingUtilities';
import { useDataService } from '../../services/ApiDataService';

const useStyles = makeStyles((theme) => ({
    icon: {
        fontSize: 14,
        paddingRight: theme.spacing(2),
    },
}));

const NameProperty = ({ icon, name }) => {
    const classes = useStyles();

    return (
        <div>
            {!!icon && <Icon className={classes.icon}>{icon}</Icon>}
            {name}
        </div>
    );
};

const propertiesDef = {
    properties: [
        {
            title: i18n._(t`Status`),
            selector: 'status',
            type: ROW_TYPES.TEXT,
        },
        {
            title: i18n._(t`Name`),
            type: ROW_TYPES.FUNCTION,
            mapper: (data) => {
                const { icon, name } = data || {};
                return <NameProperty icon={icon} name={name} />;
            },
        },
        {
            title: i18n._(t`Description`),
            type: ROW_TYPES.FUNCTION,
            mapper: (data) => {
                const { description } = data?.details || {};
                return isBlank(description) ? DEFAULT_INVALID_VALUE : <div>{description}</div>;
            },
        },
        {
            title: i18n._(t`Run Schedule`),
            selector: 'scheduling.type',
            type: ROW_TYPES.TEXT,
            formatter: _.capitalize,
        },
        runIntervalProperty,
        runTimeProperty,
        {
            title: i18n._(t`Tags`),
            type: ROW_TYPES.FUNCTION,
            mapper: (data) => {
                const { tags } = data || {};
                if (!tags?.length) return DEFAULT_INVALID_VALUE;
                return tags.join(', ');
            },
        },
        {
            title: i18n._(t`Smart Edge Monitoring`),
            type: ROW_TYPES.FUNCTION,
            mapper: (data) => {
                const { beaconPackets } = data || {};
                if (_.isUndefined(beaconPackets)) return DEFAULT_INVALID_VALUE;
                return beaconPackets ? t`Enabled` : t`Disabled`;
            },
        },
        {
            title: i18n._(t`Business Services`),
            type: ROW_TYPES.FUNCTION,
            mapper: (data) => {
                const { businessServices } = data || {};
                if (!businessServices?.length) return DEFAULT_INVALID_VALUE;
                return (
                    <div>
                        {businessServices.map(({ _id, name }) => (
                            <div key={_id}>
                                <PageLink
                                    target="/business-service-detail"
                                    targetName={name}
                                    filters={[{ name: 'BusinessService', uuid: _id }]}
                                />
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ],
};

const testDataSource = {
    url: '/query/table',
    params: {
        type: 'test',
        metrics: 'status',
    },
};

export default function PropertiesTab() {
    const test = useTest();
    const dataService = useDataService();
    const tableData = usePanelDataService(testDataSource);
    const [data, setData] = useState(null);
    const [properties, setProperties] = useState(null);

    useEffect(() => {
        const testUuid = test?.uuid;
        if (testUuid) {
            dataService.get(`/ngp/v2/service-tests/${testUuid}`, { includedDeletedOnes: true }).then((props) => {
                setProperties(props);
                const icon = tableData?.entities?.test?.testType?.details?.icon;
                const dt = {
                    ...props,
                    icon,
                };
                setData(dt);
            });
        }
    }, [dataService, tableData, test]);

    if (!properties || !tableData) return null;

    return <Properties propertiesDef={propertiesDef} data={data} />;
}
